import type { Line, Span, Style } from './types'

/** A span with no explicit style inherits the terminal foreground. */
export const span = (text: string, style?: Style): Span => (style ? { text, style } : { text })

export const link = (text: string, href: string): Span => ({ text, style: 'link', href })

export const line = (...spans: (Span | string)[]): Line => ({
  spans: spans.map((value) => (typeof value === 'string' ? { text: value } : value)),
})

/** Convenience for the very common "one flat string" case. */
export const text = (value: string, style?: Style): Line => ({ spans: [span(value, style)] })

export const blank = (): Line => ({ spans: [{ text: '' }] })

export const plain = (value: Line): string => value.spans.map((item) => item.text).join('')

/**
 * Greedy word wrap. The scrollback is `white-space: pre`, so prose longer than
 * the viewport would otherwise turn into one sideways-scrolling row.
 */
export function wrap(value: string, width = 88): string[] {
  const rows: string[] = []
  for (const paragraph of value.split('\n')) {
    let row = ''
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      if (row && row.length + 1 + word.length > width) {
        rows.push(row)
        row = word
      } else {
        row = row ? `${row} ${word}` : word
      }
    }
    rows.push(row)
  }
  return rows
}

/** Renders `values` into evenly spaced columns, the way `ls` fills a terminal. */
export function columns(values: string[], width = 80, style?: Style): Line[] {
  if (!values.length) return []
  const cell = Math.max(...values.map((value) => value.length)) + 2
  const perRow = Math.max(1, Math.floor(width / cell))
  const rows: Line[] = []
  for (let index = 0; index < values.length; index += perRow) {
    rows.push({
      spans: values
        .slice(index, index + perRow)
        .map((value) => span(value.padEnd(cell), style)),
    })
  }
  return rows
}

/** Byte counts as `ls -lh` prints them. */
export const humanBytes = (bytes: number): string => {
  const units = ['B', 'K', 'M', 'G']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${unit === 0 ? value : value.toFixed(1)}${units[unit]}`
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `MMM DD HH:MM`, the short form `ls -l` uses for recent files. */
export const shortDate = (value: Date): string =>
  `${MONTHS[value.getMonth()]} ${String(value.getDate()).padStart(2, ' ')} ${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`

/** `1d 04:31:07`, the shape `uptime` reports. */
export const duration = (seconds: number): string => {
  const days = Math.floor(seconds / 86_400)
  const hours = Math.floor((seconds % 86_400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = Math.floor(seconds % 60)
  const clock = [hours, minutes, rest].map((part) => String(part).padStart(2, '0')).join(':')
  return days ? `${days}d ${clock}` : clock
}
